// Timer global distribuido entre ventanas usando localStorage.
// Diseño:
//   - Una única ventana es "maestra" y tickea el contador. Las demás solo leen.
//   - Master election por lease con heartbeat: el maestro reescribe su heartbeat
//     cada HEARTBEAT_MS; si otro tab no ve heartbeat reciente (> MASTER_TIMEOUT_MS)
//     reclama el rol. Si dos tabs reclaman simultáneamente, gana el último en escribir,
//     porque el tab verifica `id` tras un pequeño delay antes de iniciar el intervalo.
//   - Tiempo libre de drift: cuando isRunning=true guardamos `endTimestamp`, y
//     `currentTime` se recomputa desde `Date.now()` — ni el throttling del navegador
//     en tabs en background ni la imprecisión de setInterval acumulan error.

import { socketService } from './socket';
import { STORAGE_KEYS } from './localStorage';

export interface TimerState {
  isRunning: boolean;
  currentTime: number;
  totalTime: number;
  title: string;
  subtitle: string;
  endTimestamp?: number;
}

const HEARTBEAT_MS = 2_000;
const MASTER_TIMEOUT_MS = 5_000;
const TICK_MS = 250;
const COMMAND_MAX_AGE_MS = 5_000;

const hasWindow = (): boolean => typeof window !== 'undefined';

class GlobalTimerService {
  private readonly TIMER_STATE_KEY = STORAGE_KEYS.TIMER_STATE;
  private readonly TIMER_CONTROL_KEY = STORAGE_KEYS.TIMER_CONTROL;
  private readonly TIMER_MASTER_KEY = STORAGE_KEYS.TIMER_MASTER;

  private readonly masterId = `timer_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  private tickIntervalId: ReturnType<typeof setInterval> | null = null;
  private heartbeatIntervalId: ReturnType<typeof setInterval> | null = null;
  private watchIntervalId: ReturnType<typeof setInterval> | null = null;
  private storageHandler: ((e: StorageEvent) => void) | null = null;
  private isMaster = false;
  private lastEmittedTime = -1;

  constructor() {
    if (!hasWindow()) return;
    this.tryBecomeMaster();
    this.setupControlListener();
    this.watchMaster();
  }

  private defaultState(): TimerState {
    return {
      isRunning: false,
      currentTime: 300,
      totalTime: 300,
      title: 'Tiempo de Competencia',
      subtitle: 'Neurolimpiadas 2025'
    };
  }

  private readMaster(): { id: string; heartbeat: number } | null {
    try {
      const raw = localStorage.getItem(this.TIMER_MASTER_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (typeof parsed?.id === 'string' && typeof parsed?.heartbeat === 'number') return parsed;
      return null;
    } catch {
      return null;
    }
  }

  private tryBecomeMaster(): void {
    const existing = this.readMaster();
    const now = Date.now();
    if (existing && now - existing.heartbeat < MASTER_TIMEOUT_MS) {
      this.isMaster = existing.id === this.masterId;
      if (this.isMaster) this.startTickInterval();
      this.startHeartbeat();
      return;
    }
    this.claimMaster();
  }

  private claimMaster(): void {
    try {
      localStorage.setItem(this.TIMER_MASTER_KEY, JSON.stringify({ id: this.masterId, heartbeat: Date.now() }));
    } catch (error) {
      console.error('Error reclamando maestría del timer:', error);
      return;
    }
    setTimeout(() => {
      const winner = this.readMaster();
      this.isMaster = winner?.id === this.masterId;
      if (this.isMaster) this.startTickInterval();
      this.startHeartbeat();
    }, 100);
  }

  private startHeartbeat(): void {
    if (this.heartbeatIntervalId) clearInterval(this.heartbeatIntervalId);
    this.heartbeatIntervalId = setInterval(() => {
      if (!this.isMaster) return;
      try {
        localStorage.setItem(this.TIMER_MASTER_KEY, JSON.stringify({ id: this.masterId, heartbeat: Date.now() }));
      } catch {
        // ignoramos cuota en heartbeat — otro tab tomará el relevo
      }
    }, HEARTBEAT_MS);
  }

  private watchMaster(): void {
    if (this.watchIntervalId) clearInterval(this.watchIntervalId);
    this.watchIntervalId = setInterval(() => {
      if (this.isMaster) return;
      const existing = this.readMaster();
      if (!existing || Date.now() - existing.heartbeat > MASTER_TIMEOUT_MS) {
        this.claimMaster();
      }
    }, MASTER_TIMEOUT_MS);
  }

  private startTickInterval(): void {
    if (this.tickIntervalId) clearInterval(this.tickIntervalId);
    this.tickIntervalId = setInterval(() => this.tick(), TICK_MS);
  }

  private tick(): void {
    const state = this.getTimerState();
    if (!state.isRunning) return;

    let newCurrentTime: number;
    if (typeof state.endTimestamp === 'number') {
      newCurrentTime = Math.max(0, Math.ceil((state.endTimestamp - Date.now()) / 1000));
    } else {
      // Compat con estados antiguos sin endTimestamp
      newCurrentTime = Math.max(0, state.currentTime - 1);
    }

    if (newCurrentTime === this.lastEmittedTime) return;
    this.lastEmittedTime = newCurrentTime;

    const updated: TimerState = { ...state, currentTime: newCurrentTime };
    if (newCurrentTime === 0) {
      updated.isRunning = false;
      delete updated.endTimestamp;
    }
    this.setTimerState(updated);

    socketService.emit('timer-tick', {
      currentTime: newCurrentTime,
      minutes: Math.floor(newCurrentTime / 60),
      seconds: newCurrentTime % 60,
      isRunning: updated.isRunning
    });

    if (newCurrentTime === 0) {
      socketService.emit('timer-finished', updated);
    }
  }

  private setupControlListener(): void {
    if (!hasWindow()) return;
    // Idempotente — evita duplicar el listener si destroy() + constructor se
    // reimportan por HMR, o si una llamada externa re-invoca setup.
    if (this.storageHandler) return;
    this.storageHandler = (e: StorageEvent) => {
      if (e.key !== this.TIMER_CONTROL_KEY || !e.newValue) return;
      try {
        const { command, data, timestamp } = JSON.parse(e.newValue);
        if (typeof timestamp !== 'number' || Date.now() - timestamp > COMMAND_MAX_AGE_MS) return;
        this.processTimerCommand(command, data);
      } catch (error) {
        console.error('Error procesando comando de timer:', error);
      }
    };
    window.addEventListener('storage', this.storageHandler);
  }

  private processTimerCommand(command: string, data?: any): void {
    const currentState = this.getTimerState();
    const now = Date.now();
    let newState: TimerState;

    switch (command) {
      case 'start':
        newState = {
          ...currentState,
          isRunning: true,
          endTimestamp: now + currentState.currentTime * 1000
        };
        this.setTimerState(newState);
        this.lastEmittedTime = -1;
        socketService.emit('timer-started', newState);
        break;
      case 'pause':
        newState = { ...currentState, isRunning: false };
        delete newState.endTimestamp;
        this.setTimerState(newState);
        socketService.emit('timer-paused', newState);
        break;
      case 'reset': {
        const total = typeof data?.totalTime === 'number' && data.totalTime >= 0
          ? data.totalTime
          : (currentState.totalTime || 300);
        newState = {
          ...currentState,
          isRunning: false,
          currentTime: total,
          totalTime: total
        };
        delete newState.endTimestamp;
        this.setTimerState(newState);
        socketService.emit('timer-reset', newState);
        break;
      }
      case 'update':
        newState = { ...currentState, ...data };
        this.setTimerState(newState);
        socketService.emit('timer-updated', newState);
        break;
      default:
        return;
    }
  }

  getTimerState(): TimerState {
    if (!hasWindow()) return this.defaultState();
    try {
      const savedState = localStorage.getItem(this.TIMER_STATE_KEY);
      if (!savedState) return this.defaultState();
      const parsed = JSON.parse(savedState) as TimerState;
      if (parsed.isRunning && typeof parsed.endTimestamp === 'number') {
        const remaining = Math.max(0, Math.ceil((parsed.endTimestamp - Date.now()) / 1000));
        parsed.currentTime = remaining;
        if (remaining === 0) parsed.isRunning = false;
      }
      return parsed;
    } catch (error) {
      console.error('Error al cargar estado del timer:', error);
      return this.defaultState();
    }
  }

  setTimerState(state: TimerState): void {
    if (!hasWindow()) return;
    try {
      localStorage.setItem(this.TIMER_STATE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error('Error al guardar estado del timer:', error);
    }
  }

  sendCommand(command: string, data?: any): void {
    if (!hasWindow()) return;
    const controlData = { command, data, timestamp: Date.now(), source: window.location.href };
    try {
      localStorage.setItem(this.TIMER_CONTROL_KEY, JSON.stringify(controlData));
    } catch (error) {
      console.error('Error enviando comando timer:', error);
    }
    // storage event no llega a la propia ventana, así que ejecutamos también aquí
    this.processTimerCommand(command, data);
  }

  start(): void { this.sendCommand('start'); }
  pause(): void { this.sendCommand('pause'); }
  reset(totalTime?: number): void { this.sendCommand('reset', { totalTime }); }
  updateTimer(updates: Partial<TimerState>): void { this.sendCommand('update', updates); }

  subscribe(_callback: (state: TimerState) => void): () => void {
    // Compat: los consumidores escuchan vía socketService.on('timer-tick', ...).
    return () => {};
  }

  destroy(): void {
    if (this.tickIntervalId) { clearInterval(this.tickIntervalId); this.tickIntervalId = null; }
    if (this.heartbeatIntervalId) { clearInterval(this.heartbeatIntervalId); this.heartbeatIntervalId = null; }
    if (this.watchIntervalId) { clearInterval(this.watchIntervalId); this.watchIntervalId = null; }
    if (this.storageHandler && hasWindow()) {
      window.removeEventListener('storage', this.storageHandler);
      this.storageHandler = null;
    }
    if (this.isMaster && hasWindow()) {
      try { localStorage.removeItem(this.TIMER_MASTER_KEY); } catch { /* noop */ }
    }
    this.isMaster = false;
  }
}

export const globalTimerService = new GlobalTimerService();
